import { useEffect, useContext, useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Col, Row } from "reactstrap";
import { GoDotFill } from "react-icons/go";
import { BsCalendarCheck, BsClock, BsCheckCircle } from "react-icons/bs";
import { FaClock, FaStethoscope } from "react-icons/fa";
import { RxPerson } from "react-icons/rx";
import { GoDeviceCameraVideo } from "react-icons/go";
import {
  _imagesURL,
  ReadNotificationsApi,
  getNotificationsApi,
} from "../../constants/API";
import Avatar from "../../assets/images/icon/placeholder_Awatar.png";
import Place from "../../assets/images/place.png";
import { toast } from "react-toastify";
import {
  EVENT_DETAILS,
  MENTORSHIP,
  NEW_COURSE_DETAILS,
  POST_DETAILS,
  PROFILE,
} from "../../constants/RoutesUrl";
import { Language } from "../../App";
import ImageComponent from "../common/ImageComponent";
import { CountContext } from "../../constants/ProtectedRoute";
import { authorize } from "../../shared/authorize";
import { getSplashData } from "../../services/api";
import { useQuery } from "react-query";
import { GetNotificationBody } from "../common/GetNotificationBody";
import NotificationSkeleton from "./NotificationSkeletonWave";
import PurchasedUsersModal from "../course-details/PurchasedUsersModal";
import "./Notification.css";
import { CiCalendar } from "react-icons/ci";
import {
  IoPersonOutline,
  IoNewspaperOutline,
  IoVideocamOutline,
  IoFlaskOutline,
  IoBookOutline,
  IoCalendarOutline,
  IoPulseOutline,
  IoWalletOutline,
  IoNotificationsOutline,
  IoSchoolOutline,
} from "react-icons/io5";
import { LuCalendarDays, LuStethoscope } from "react-icons/lu";

const Notification = () => {
  const { count } = useContext(CountContext);
  const savedNotificationType = sessionStorage.getItem("lastNotificationType");
  const returnFromNavigation = sessionStorage.getItem("returnFromNotificationNavigation");

  const { lang } = useContext(Language);
  const { counts = {} } = count ?? {};

  const btns = [
    { id: 1, type: "social", name: lang?.posts, count: counts?.social },
    { id: 2, type: "webinar", name: lang?.webinars, count: counts?.webinar },
    {
      id: 3,
      type: "clinical",
      name: lang?.clinical_studies,
      count: counts?.clinical,
    },
    { id: 4, type: "course", name: lang?.courses, count: counts?.course },
    { id: 5, type: "event", name: lang?.events, count: counts?.event },
    {
      id: 6,
      type: "clinical-session",
      name: lang?.clinical_sessions,
      count: counts?.clinical_session,
    },
    {
      id: 7,
      type: "Wallet",
      name: lang?.wallet,
      count: counts?.wallet,
    },
    {
      id: 8,
      type: "mentor",
      name: lang?.mentorship_consultation_tab || "Consultation",
      
      count: counts?.mentor,
    },
  ];

  // Function to get the active button based on notification type
  const getActiveBtnFromType = (type) => {
    const btn = btns.find(btn => btn.type === type);
    return btn ? btn.id : 1;
  };

  // Initialize state with proper values immediately
  const getInitialState = () => {
    if (returnFromNavigation === "true" && savedNotificationType) {
      return {
        activeBtn: getActiveBtnFromType(savedNotificationType),
        notificationsType: savedNotificationType
      };
    } else {
      return {
        activeBtn: 1,
        notificationsType: "social"
      };
    }
  };

  const initialState = getInitialState();

  // States - Initialize with correct values from the start
  const [dataLoading, setDataLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [activeBtn, setActiveBtn] = useState(initialState.activeBtn);
  const [notificationsType, setNotificationsType] = useState(initialState.notificationsType);
  const [notificationData, setNotificationData] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);
  const { refetch } = useQuery("splashData", getSplashData);
  const scrollSentinelRef = useRef(null);
  const hasMorePages = page < lastPage;

  // Handle sessionStorage cleanup and initialization
  useEffect(() => {
    if (returnFromNavigation === "true" && savedNotificationType) {
      // User is returning from a navigation, restore the last active type
      const restoredActiveBtn = getActiveBtnFromType(savedNotificationType);
      setNotificationsType(savedNotificationType);
      setActiveBtn(restoredActiveBtn);
      // Clear the flag after using it
      sessionStorage.removeItem("returnFromNotificationNavigation");
    } else {
      // First time visit or coming from other routes, default to social/posts
      setNotificationsType("social");
      setActiveBtn(1);
      // Clear any existing saved type
      sessionStorage.removeItem("lastNotificationType");
    }
    setIsInitialized(true);
  }, []);

  const handleReadNotification = () => {
    axios
      .post(ReadNotificationsApi, { type: notificationsType }, authorize())
      .then(() => {
        refetch()
      })
      .catch((err) => {
        toast.error(err.message || lang?.notifications_mark_read_failed || "Failed to mark notifications as read");
      });
  };

  // pageNum 1 replaces the list (tab switch / first load) and shows the
  // full-page shimmer; any later page appends onto the existing list and
  // shows the smaller "loading more" shimmer instead.
  const handleGetNotifications = (pageNum = 1) => {
    if (pageNum === 1) {
      setDataLoading(true);
    } else {
      setLoadingMore(true);
    }
    axios
      .post(getNotificationsApi, { type: notificationsType, page: pageNum }, authorize())
      .then((res) => {
        if (res.data.status) {
          const incoming = res.data.data;
          setNotificationData((prev) =>
            pageNum === 1
              ? incoming
              : { ...incoming, data: [...(prev.data || []), ...(incoming.data || [])] }
          );
          setPage(incoming.current_page || pageNum);
          setLastPage(incoming.last_page || pageNum);
          if (pageNum === 1) handleReadNotification();
        } else {
          toast.error(res.data.action);
        }
        setDataLoading(false);
        setLoadingMore(false);
      })
      .catch((err) => {
        setDataLoading(false);
        setLoadingMore(false);
        toast.error(err);
      });
  };

  // Only fetch notifications after component is properly initialized
  useEffect(() => {
    if (isInitialized) {
      setPage(1);
      setLastPage(1);
      handleGetNotifications(1);
    }
  }, [notificationsType, isInitialized]);

  // Infinite scroll: once the sentinel below the list scrolls into view,
  // fetch the next page — same shimmer-then-append flow as above, and it
  // just stops firing once page reaches lastPage (data.data.length === total).
  useEffect(() => {
    const node = scrollSentinelRef.current;
    if (!node || !hasMorePages || dataLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMore) {
          handleGetNotifications(page + 1);
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [page, lastPage, loadingMore, dataLoading, notificationsType]);

  // Function to handle tab change
  const handleTabChange = (btnId, type) => {
    setActiveBtn(btnId);
    setNotificationsType(type);
    // Save the current notification type when user manually changes tabs
    sessionStorage.setItem("lastNotificationType", type);
  };

  // Don't render until properly initialized to prevent flash
  if (!isInitialized) {
    return (
      <Row className="justify-content-center notification-onest">
        <Col lg={6} className="pb-4" style={{ paddingTop: "4rem" }}>
          <div className="bg-white rounded mb-5 p-3 border border-1 border-inherit">
            <div className="text-center pt-3">
              {Array.from({ length: 5 }).map((_, index) => (
                <NotificationSkeleton key={index} notificationsType="social" />
              ))}
            </div>
          </div>
        </Col>
      </Row>
    );
  }

  return (
    <Row className="justify-content-center notification-onest">
      <Col lg={6} className="pb-4" style={{ paddingTop: "4rem" }}>
        <div className="bg-white rounded mb-5 p-3 border border-1 border-inherit">
          {/* Tabs menu */}
          <div className="scroll-hide d-flex gap-2 mb-3 overflowX-auto">
            {btns &&
              btns.map((value) => (
                <div key={value.id}>
                  <button
                    type="button"
                    style={{ whiteSpace: "nowrap" }}
                    onClick={() => handleTabChange(value.id, value.type)}
                    className={`rounded px-5 py-2 fw-bold position-relative manue-hover hover-blue ${activeBtn === value.id
                        ? "bg-dark text-white border-0"
                        : "border border-1 border-inherit"
                      }`}
                  >
                    {value.name}{" "}
                    {value.count !== 0 && value.count ?
                      (
                        <span>
                          <div
                            className="bg-danger rounded-circle position-absolute top-0 end-0 me-4 mt-1"
                            style={{
                              zIndex: "2",
                              width: "15px",
                              height: "15px",
                            }}
                          >
                            <p
                              className="fw-bold text-white mb-0"
                              style={{ fontSize: "10px" }}
                            >
                              {value.count !== 0 && value.count}
                            </p>
                          </div>
                        </span>
                      ) : (
                        <></>
                      )}
                  </button>
                </div>
              ))}
          </div>
          {/* Tabs menu ended */}

          {dataLoading ? (
            <div className="text-center pt-3">
              {Array.from({ length: 15 }).map((_, index) =>
                notificationsType === "mentor" ? (
                  <NotificationCardMentorSkeleton key={index} />
                ) : (
                  <NotificationSkeleton key={index}
                    notificationsType={notificationsType}
                  />
                )
              )}
            </div>
          ) : notificationData.data && notificationData.data.length === 0 ? (
            <NotificationEmptyState
              notificationsType={notificationsType}
              typeName={btns.find((b) => b.type === notificationsType)?.name}
            />
          ) : (
            <>
              {notificationData.data &&
                notificationData.data.map((value) => (
                  <>
                    {value.data.type === "mentor" ? (
                      <>
                        {value.first && (
                          <p
                            className="text-uppercase mb-2"
                            style={{
                              color: "#8F98A3",
                              fontSize: "11px",
                              fontWeight: 600,
                              letterSpacing: "0.5px",
                            }}
                          >
                            {value.date}
                          </p>
                        )}
                        <NotificationCardMentor
                          key={value.id}
                          value={value}
                          currentNotificationType={notificationsType}
                        />
                      </>
                    ) : value.data.notification_type === "follow" ? (
                      <NotificationCardFollow
                        key={value.id}
                        value={value}
                        currentNotificationType={notificationsType}
                      />
                    ) : (
                      <NotificationCardLike
                        key={value.id}
                        value={value}
                        currentNotificationType={notificationsType}
                      />
                    )}
                  </>
                ))}

              {loadingMore && (
                <div className="text-center pt-1">
                  {Array.from({ length: 3 }).map((_, index) =>
                    notificationsType === "mentor" ? (
                      <NotificationCardMentorSkeleton key={`more-${index}`} />
                    ) : (
                      <NotificationSkeleton key={`more-${index}`}
                        notificationsType={notificationsType}
                      />
                    )
                  )}
                </div>
              )}

              {hasMorePages && <div ref={scrollSentinelRef} style={{ height: "1px" }} />}
            </>
          )}
        </div>
      </Col>
    </Row>
  );
};

export default Notification;

// API user names sometimes come back lowercase ("dr jim turner") — this
// capitalizes just the first letter of each word, leaving the rest of the
// word untouched so it doesn't fight with names that already have internal
// capitals (e.g. "McDonald").
const capitalizeWords = (str) => {
  if (!str) return str;
  return str
    .split(" ")
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(" ");
};

// One tab-flavored icon each, purely decorative — falls back to a generic
// bell for any type not listed here.
const EMPTY_STATE_ICONS = {
  social: IoNewspaperOutline,
  webinar: IoVideocamOutline,
  clinical: IoFlaskOutline,
  course: IoBookOutline,
  event: IoCalendarOutline,
  "clinical-session": IoPulseOutline,
  Wallet: IoWalletOutline,
  mentor: FaStethoscope,
};

// Shown in place of the list once a tab's page-1 fetch comes back with zero
// items — same empty state shape for every tab, just the icon and the
// {{type}} name in the subtitle change.
const NotificationEmptyState = ({ notificationsType, typeName }) => {
  const { lang } = useContext(Language);
  const EmptyIcon = EMPTY_STATE_ICONS[notificationsType] || IoNotificationsOutline;
  const subtitleTemplate =
    lang?.notifications_empty_subtitle ||
    "New {{type}} notifications will show up here as they happen.";

  return (
    <div className="text-center" style={{ padding: "72px 20px" }}>
      <div
        className="d-inline-flex align-items-center justify-content-center mb-3"
        style={{
          width: "80px",
          height: "80px",
          borderRadius: "50%",
          background: "radial-gradient(circle at top left, #EAFAFD 0%, #EAFAFD 20%, #FFFFFF 40%, #FFFFFF 100%)",
           border: "1px solid #DCEEF3",
        }}
      >
        <EmptyIcon size={34} color="#00BADA" />
      </div>
      <p className="fw-600 fs-16 mb-1" style={{ color: "#131722" }}>
        {lang?.notifications_empty_title || "No notifications yet"}
      </p>
      <p
        className="fs-13 mb-0 mx-auto"
        style={{ color: "#8F98A3", maxWidth: "280px", lineHeight: "20px" }}
      >
        {subtitleTemplate.replace("{{type}}", (typeName || "").toLowerCase())}
      </p>
    </div>
  );
};

export function UnixToRelative({ unixTimestamp }) {
  const currentTimestamp = Math.floor(Date.now() / 1000);
  const timestampDifference = currentTimestamp - unixTimestamp;
  const { lang } = useContext(Language);

  const intervals = [
    { singular: lang?.year, plural: lang?.years, seconds: 31536000 },
    { singular: lang?.month, plural: lang?.months, seconds: 2592000 },
    { singular: lang?.day, plural: lang?.days, seconds: 86400 },
    { singular: lang?.hour, plural: lang?.hours, seconds: 3600 },
    { singular: lang?.minute, plural: lang?.minutes, seconds: 60 },
  ];

  for (let i = 0; i < intervals.length; i++) {
    const interval = intervals[i];
    const intervalCount = Math.floor(timestampDifference / interval.seconds);

    if (intervalCount >= 1) {
      const label = intervalCount === 1 ? interval.singular : interval.plural;

      return navigator.language.startsWith("en")
        ? `${intervalCount} ${label} ${lang?.ago}`
        : `${lang?.ago} ${intervalCount} ${label}`;
    }
  }

  return `${lang?.just_now}`;
}


const NotificationCardFollow = ({ value, currentNotificationType }) => {
  const { lang } = useContext(Language);
  const Navigate = useNavigate();

  const handleNavigation = (path) => {
    // Save current notification type and set return flag
    sessionStorage.setItem("lastNotificationType", currentNotificationType);
    sessionStorage.setItem("returnFromNotificationNavigation", "true");
    Navigate(path);
  };

  return (
    <>
      <div
        className="border border-1 border-inherit mb-3 rounded overflow-hidden cursor"
        onClick={() => handleNavigation(PROFILE + "/" + value.users[0].id)}
        key={value.id}
      >
        <div className="border-bottom border-inherit border-1">
          <h6 className="mx-3 my-2 pb-0">
            <GoDotFill color="#00c5de" /> {value.date}
          </h6>
        </div>
        <div className="px-3 py-3 manue-hover">
          <div className="d-flex gap-3">
            {value.users_count > 1 ? (
              <div className="d-flex">
                <div>
                  <div
                    className="bg-white rounded-1 dropdown-avatar-shadow"
                    style={{
                      maxWidth: "3rem",
                      maxHeight: "3rem",
                      padding: "0.8px",
                    }}
                  >
                    <ImageComponent
                      src={
                        value?.users[0]?.thumbnail
                          ? _imagesURL + value.users[0].thumbnail
                          : Avatar
                      }
                      compClassName="rounded-1 img-fluid"
                      loader_height="100%"
                      loader_width="100%"
                      fallbackSrc={Place}
                    />
                  </div>
                </div>
                <div
                  style={{
                    marginLeft: "-1.7rem",
                    marginTop: "1rem",
                  }}
                >
                  <div
                    className="bg-white rounded-1 dropdown-avatar-shadow"
                    style={{
                      maxWidth: "3rem",
                      maxHeight: "3rem",
                      padding: "0.8px",
                    }}
                  >
                    <ImageComponent
                      src={
                        value?.users[1]?.thumbnail
                          ? _imagesURL + value.users[1].thumbnail
                          : Avatar
                      }
                      compClassName="rounded-1 img-fluid"
                      loader_height="100%"
                      loader_width="100%"
                      fallbackSrc={Place}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <div>
                  <div
                    className="bg-white rounded-1 dropdown-avatar-shadow"
                    style={{
                      maxWidth: "3rem",
                      maxHeight: "3rem",
                      padding: "0.8px",
                    }}
                  >
                    <ImageComponent
                      src={
                        value?.users[0]?.thumbnail
                          ? _imagesURL + value.users[0].thumbnail
                          : Avatar
                      }
                      compClassName="rounded-1 img-fluid"
                      loader_height="100%"
                      loader_width="100%"
                      fallbackSrc={Place}
                    />
                  </div>
                </div>
              </div>
            )}
            <div className="align-self-center">
              <div className="d-flex">
                {value.users_count > 1 ? (
                  <p className="mb-0 fs-md-15">
                    <span className="fw-bold">{capitalizeWords(value?.users[0].name)}</span> {lang?.and || "and"}{" "}
                    <span className="fw-bold">
                      {value.users_count > 2 ? (lang?.others || "others") : capitalizeWords(value?.users[1].name)}
                    </span>
                    {" "} {<GetNotificationBody notificationType={value.data.notification_type} type={value.data.type} />}
                  </p>
                ) : (
                  <p className="mb-0 fs-md-15">
                    <span className="fw-bold">{capitalizeWords(value?.users[0].name)}</span>{" "}
                    {" "} {<GetNotificationBody notificationType={value.data.notification_type} type={value.data.type} />}
                  </p>
                )}
              </div>
              <div>
                <small className="text-secondary">
                  <UnixToRelative unixTimestamp={value.data.time} />
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const NotificationCardLike = ({ value, currentNotificationType }) => {
  const{lang} = useContext(Language)
  const Navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const handleNavigation = (path) => {
    // Save current notification type and set return flag
    sessionStorage.setItem("lastNotificationType", currentNotificationType);
    sessionStorage.setItem("returnFromNotificationNavigation", "true");
    Navigate(path);
  };

  const handleProfileNavigation = (userId) => {
    handleNavigation(PROFILE + "/" + userId);
  };

  const handleMediaNavigation = () => {
    let path;
    if (value.data.type === "event") {
      path = EVENT_DETAILS + value.data.post_id;
    } else if (value.data.type === "course") {
      sessionStorage.setItem("CourseId", value.data.post_id);
      path = NEW_COURSE_DETAILS + value.data.post_id;
    } else {
      path = POST_DETAILS + value.data.post_id;
    }
    handleNavigation(path);
  };

  return (
    <>
      <div
        className="border border-1 border-inherit mb-3 rounded overflow-hidden"
        key={value.id}
      >
        <div className="border-bottom border-inherit border-1">
          <h6 className="mx-3 my-2 pb-0">
            <GoDotFill color="#00c5de" /> {value.date}
          </h6>
        </div>
        <div className="px-3 py-3 manue-hover">
          <div className="d-flex justify-content-between">
            <div>
              <div className="d-flex gap-3">
                {value.users_count > 1 ? (
                  <div className="d-flex">
                    <div>
                      <div
                        className="bg-white rounded-1 dropdown-avatar-shadow"
                        style={{
                          maxWidth: "3rem",
                          maxHeight: "3rem",
                          padding: "0.8px",

                        }}
                      >
                        <ImageComponent
                          src={
                            value?.users[0]?.thumbnail
                              ? _imagesURL + value.users[0].thumbnail
                              : Avatar
                          }
                          compClassName="rounded-1 img-fluid"
                          loader_height="100%"
                          loader_width="100%"
                          fallbackSrc={Place}
                        />
                      </div>
                    </div>
                    <div
                      style={{
                        marginLeft: "-1.7rem",
                        marginTop: "1rem",
                      }}
                    >
                      <div
                        className="bg-white rounded-1 dropdown-avatar-shadow"
                        style={{
                          maxWidth: "3rem",
                          maxHeight: "3rem",
                          padding: "0.8px",
                        }}
                      >
                        <ImageComponent
                          src={
                            value?.users[1]?.thumbnail
                              ? _imagesURL + value.users[1].thumbnail
                              : Avatar
                          }
                          compClassName="rounded-1 img-fluid"
                          loader_height="100%"
                          loader_width="100%"
                          fallbackSrc={Place}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div>
                      <div
                        className="bg-white rounded-1 dropdown-avatar-shadow"
                        style={{
                          maxWidth: "3rem",
                          maxHeight: "3rem",
                          padding: "0.8px",
                          cursor: "pointer"
                        }}
                        onClick={() => handleProfileNavigation(value.users[0].id)}
                      >
                        <ImageComponent
                          src={
                            value?.users[0]?.thumbnail
                              ? _imagesURL + value.users[0].thumbnail
                              : Avatar
                          }
                          compClassName="rounded-1 img-fluid"
                          loader_height="100%"
                          loader_width="100%"
                          fallbackSrc={Place}
                        />
                      </div>
                    </div>
                  </div>
                )}
                <div className="align-self-center">
                  <div className="d-flex">
                    {value.users_count > 1 ? (
                      <p className="mb-0 fs-md-15">
                        <span className="fw-bold cursor-pointer" onClick={() => handleProfileNavigation(value.users[0].id)}>
                          {capitalizeWords(value?.users[0].name)}
                        </span> {lang.and}{" "}
                        <span className="fw-bold cursor-pointer" onClick={() => {
                          if (value.users_count > 2) {
                            setIsOpen(true)
                          } else {
                            handleProfileNavigation(value.users[1].id);
                          }
                        }}>
                          {value.users_count > 2 ? lang.others : capitalizeWords(value?.users[1].name)}
                        </span>
                        {" "}{<GetNotificationBody notificationType={value.data.notification_type} type={value.data.type} />}
                      </p>
                    ) : (
                      <p className="mb-0 fs-md-15">
                        <span className="fw-bold cursor-pointer" onClick={() => handleProfileNavigation(value.users[0].id)}>
                          {capitalizeWords(value?.users[0].name)}
                        </span>
                        {" "}  {<GetNotificationBody notificationType={value.data.notification_type} type={value.data.type} />}
                      </p>
                    )}
                  </div>
                  <div>
                    <small className="text-secondary">
                      <UnixToRelative unixTimestamp={value.data.time} />
                    </small>
                  </div>
                </div>
              </div>
            </div>
            {value.data.media && (
              <div>
                <div>
                  <div
                    className="bg-white rounded-1 dropdown-avatar-shadow cursor"
                    onClick={handleMediaNavigation}
                    style={{
                      maxWidth: "2.5rem",
                      maxHeight: "2.5rem",
                      padding: "0.8px",
                    }}
                  >
                    <img
                      src={_imagesURL + value.data.media}
                      className="rounded-1"
                      style={{
                        width: "2.5rem",
                        height: "2.5rem",
                        objectFit: "cover",
                      }}
                      alt={lang?.image || "picture"}
                      onError={(e) => {
                        if (e.target.src !== Place) e.target.src = Place;
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <PurchasedUsersModal isOpen={isOpen} toggle={() => setIsOpen(!isOpen)} courseId={value.data.post_id} />
    </>
  );
};

// Consultation tab (data.type === "mentor"). notification_type is the base
// event ("request"/"schedule"/"reminder"/"start"/"complete") with a
// "_as_mentee" suffix when the current user is the mentee side of that same
// event — the plain (no-suffix) value always means "as mentor".
const MENTOR_NOTIF_TITLE_KEYS = {
  request: "mentorship_notif_title_request",
  schedule: "mentorship_notif_title_schedule",
  reminder: "mentorship_notif_title_reminder",
  start: "mentorship_notif_title_start",
  complete: "mentorship_notif_title_complete",
};

// Top-right timestamp on the mentor card reads as elapsed time ("20 min
// ago", "14 hr ago", "1 day ago", "2 days ago", "1 week ago") rather than a
// clock time — same "ago" word-order flip UnixToRelative above uses
// (English trails it, everything else leads with it, e.g. "hace 20 min").
const formatNotificationTime = (unixSeconds, lang) => {
  const timestamp = Number(unixSeconds);
  if (!timestamp) return "";
  const diffSeconds = Math.max(Math.floor(Date.now() / 1000 - timestamp), 0);
  const ago = lang?.ago || "ago";
  const isEnglish = navigator.language.startsWith("en");

  if (diffSeconds < 60) return lang?.just_now || "Just now";

  const build = (count, singular, plural) => {
    const label = count === 1 ? singular : plural;
    return isEnglish ? `${count} ${label} ${ago}` : `${ago} ${count} ${label}`;
  };

  if (diffSeconds < 3600) {
    return build(Math.floor(diffSeconds / 60), lang?.min || "min", lang?.mins || "mins");
  }
  if (diffSeconds < 86400) {
    return build(Math.floor(diffSeconds / 3600), lang?.hr || "hr", lang?.hrs || "hrs");
  }
  if (diffSeconds < 604800) {
    return build(Math.floor(diffSeconds / 86400), lang?.day || "day", lang?.days || "days");
  }
  return build(Math.floor(diffSeconds / 604800), lang?.week || "week", lang?.weeks || "weeks");
};

// Formats a unix-seconds timestamp — the caller passes sessionStartTimestamp
// (mentor_request.start_timestamp when the API includes it, else data.time)
// — into "Jul 23, 2026 at 1:00 AM (GMT+2)" for the schedule card's custom
// sentence.
const formatScheduleDateTime = (unixSeconds) => {
  const timestamp = Number(unixSeconds);
  if (!timestamp) return "";
  const date = new Date(timestamp * 1000);
  const datePart = date.toLocaleDateString(navigator.language, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const timePart = date.toLocaleTimeString(navigator.language, {
    hour: "numeric",
    minute: "2-digit",
  });
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
  return `${datePart} at ${timePart} (GMT${sign}${offsetHours})`;
};

// Same sessionStartTimestamp source as formatScheduleDateTime, but just the
// clock time, for the reminder card's "...is scheduled for today at 3:25
// PM" sentence.
const formatTimeOnly = (unixSeconds) => {
  const timestamp = Number(unixSeconds);
  if (!timestamp) return "";
  return new Date(timestamp * 1000).toLocaleTimeString(navigator.language, {
    hour: "numeric",
    minute: "2-digit",
  });
};

const NotificationCardMentor = ({ value, currentNotificationType }) => {
  const { lang } = useContext(Language);
  const Navigate = useNavigate();

  const notificationType = value.data.notification_type || "";
  const isMentee = notificationType.endsWith("_as_mentee");
  const baseType = isMentee
    ? notificationType.replace("_as_mentee", "")
    : notificationType;
  const user = value?.users?.[0];

  const roleLabel = isMentee
    ? lang?.mentorship_as_mentee_label || "As Mentee"
    : lang?.mentorship_as_mentor_label || "As Mentor";
  const RoleIcon = isMentee ? IoPersonOutline: LuStethoscope;

  // "schedule", "start", "complete", "request" and "reminder" all get a
  // custom, name-highlighted, fully localized sentence (matches the design
  // reference) instead of the raw API body — raw API text can't be
  // translated by our lang files, it's whatever language the backend sent
  // it in. Every other notification_type still just shows data.body as-is.
  const isSchedule = baseType === "schedule";
  const isStart = baseType === "start";
  const isComplete = baseType === "complete";
  const isRequest = baseType === "request";
  // Reminder cards swap the mentor/mentee role badge for a fixed "15 Minutes
  // Left" pill and always show the clock icon — the API gives no exact
  // session start time here (just a pre-formatted body sentence), so this
  // stays a static label rather than a live countdown.
  const isReminder = baseType === "reminder";
  // "approved" is a standalone system notification about the mentor's own
  // application — no mentor/mentee side to it, so it carries no role badge
  // at all and always shows the graduation-cap icon (never a user photo).
  const isApproved = baseType === "approved";

  // schedule_as_mentee ("Request Accepted") and request_as_mentee ("Request
  // Submitted") both read differently from their mentor-side title, so
  // they're checked ahead of the generic title-key lookup.
  const title = isApproved
    ? lang?.mentorship_notif_approved_title || "You're a Mentor now! 🎉"
    : isSchedule && isMentee
      ? lang?.mentorship_notif_title_schedule_as_mentee || "Request Accepted"
      : isRequest && isMentee
        ? lang?.mentorship_notif_title_request_as_mentee || "Request Submitted"
        : lang?.[MENTOR_NOTIF_TITLE_KEYS[baseType]] ||
          lang?.mentorship_notif_title_generic ||
          "Mentorship Update";

  // "request" can bundle more than one requester into a single notification
  // (value.users_count > 1), but the card always shows just the first
  // requester's name — no "and Y" / "and others" suffix.
  const requestUsers = value?.users || [];
  const renderRequestUsers = () => (
    <span className="fw-500" style={{ color: "#00BADA" }}>
      {capitalizeWords(requestUsers[0]?.name)}
    </span>
  );
  // The notification's own data.time is just when the notification itself
  // was created — the actual session start now comes from the nested
  // mentor_request.start_timestamp when the API includes it, falling back
  // to data.time only if it doesn't.
  const sessionStartTimestamp = value.data.mentor_request?.start_timestamp || value.data.time;
  const scheduleDateTime = isSchedule
    ? formatScheduleDateTime(sessionStartTimestamp)
    : "";

  const handleNavigation = () => {
    // Save current notification type and set return flag
    sessionStorage.setItem("lastNotificationType", currentNotificationType);
    sessionStorage.setItem("returnFromNotificationNavigation", "true");
    Navigate(MENTORSHIP);
  };

  return (
    <div
      className="border border-1 border-inherit mb-3 overflow-hidden cursor manue-hover"
      onClick={handleNavigation}
      style={{
        borderRadius: "20px",
        ...(isReminder && {
          background: "#7E57C21A",
          border: "1px solid #7E57C233",
        }),
        ...(isStart && {
          background: "#E8FBFF",
          border: "1px solid #1DC1DD33",
        }),
      }}
      key={value.id}
    >
      <div className="px-3 py-3">
        <div className="d-flex justify-content-between align-items-start gap-2">
          <div className="d-flex gap-3">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 overflow-hidden"
              style={{
                width: "3rem",
                height: "3rem",
                background: isReminder
                  ? "#7C5CFC1A"
                  : isApproved || (isRequest && isMentee) || isStart
                    ? "#00BADA1A"
                    : "#15803D1A",
              }}
            >

              {/* approved: always the graduation-cap icon (no counterpart
                  user at all). request_as_mentee: always the checkmark icon,
                  never the recipient mentor's photo — matches the design
                  reference. */}
              {isReminder ? (
                <BsClock size={22} color="#7C5CFC" />
              ) : isApproved ? (
                <IoSchoolOutline size={30} color="#00BADA" />
              ) : isRequest && isMentee ? (
                <BsCheckCircle size={22} color="#00BADA" />
              ) : isStart ? (
                <GoDeviceCameraVideo size={22} color="#00BADA" />
              ) : user?.image && !(isSchedule && !isMentee) ? (
                <ImageComponent
                  src={_imagesURL + user.image}
                  compClassName="rounded-circle w-100 h-100 object-fit-cover"
                  loader_height="100%"
                  loader_width="100%"
                  fallbackSrc={Place}
                />
              ) : (
               <LuCalendarDays size={25} color="#15803D" />
              )}
            </div>
            <div>
              {/* approved carries no mentor/mentee side — no role badge. */}
              {!isApproved && (
                <span
                  className="d-inline-flex align-items-center gap-1 rounded-pill px-2 py-1 mb-1 fs-11"
                  style={{
                    color: isReminder || (isStart && !isMentee) ? "#fff" : "#061223",
                    background: isReminder
                      ? "#7E57C2"
                      : isStart && !isMentee
                        ? "#00BADA"
                        : isMentee
                          ? "#F4F4F4"
                          : "#E8FBFF",
                    fontWeight: 500,
                  }}
                >
                  {isReminder ? <FaClock size={12} /> : <RoleIcon size={12} />}{" "}
                  {isReminder
                    ? lang?.mentorship_notif_reminder_badge || "15 Minutes Left"
                    : roleLabel}
                </span>
              )}
              <p className="mb-1 fw-600 fs-15">{title}</p>
              {isApproved ? (
                <p className="mb-0 fs-14 text-[#5F6673]">
                  {lang?.mentorship_notif_approved_prefix ||
                    "Congratulations! Your application has been"}{" "}
                  <span className="fw-500" style={{ color: "#00BADA" }}>
                    {lang?.mentorship_notif_approved_highlight || "approved"}
                  </span>
                  {". "}
                  {lang?.mentorship_notif_approved_suffix ||
                    "You can now start accepting mentees and scheduling sessions."}
                </p>
              ) : isSchedule ? (
                <p className="mb-0 fs-14 text-[#5F6673]">
                  {isMentee ? (
                    <>
                      <span className="fw-500" style={{ color: "#00BADA" }}>
                        {capitalizeWords(user?.name)}
                      </span>{" "}
                      {lang?.mentorship_notif_schedule_as_mentee_text ||
                        "accepted your mentorship request and confirmed your session"}{" "}
                      {scheduleDateTime}
                    </>
                  ) : (
                    <>
                      {lang?.mentorship_notif_schedule_prefix ||
                        "You confirmed a session with"}{" "}
                      <span className="fw-500" style={{ color: "#00BADA" }}>
                        {capitalizeWords(user?.name)}
                      </span>
                      {" • "}
                      {scheduleDateTime}
                    </>
                  )}
                </p>
              ) : isStart ? (
                <p className="mb-0 fs-14 text-[#5F6673] ">
                  {lang?.mentorship_notif_start_prefix || "Your session with"}{" "}
                  <span className="fw-500" style={{ color: "#00BADA" }}>
                    {capitalizeWords(user?.name)}
                  </span>{" "}
                  {lang?.mentorship_notif_start_suffix || "has started. Join now."}
                </p>
              ) : isComplete ? (
                <p className="mb-0 fs-14 text-[#5F6673]">
                  {lang?.mentorship_notif_complete_prefix ||
                    "Your mentorship session with"}{" "}
                  <span className="fw-500" style={{ color: "#00BADA" }}>
                    {capitalizeWords(user?.name)}
                  </span>{" "}
                  {isMentee
                    ? lang?.mentorship_notif_complete_as_mentee_suffix ||
                      "has been completed. We hope it was helpful!"
                    : lang?.mentorship_notif_complete_suffix ||
                      "has been completed."}
                </p>
              ) : isRequest ? (
                <p className="mb-0 fs-14 text-[#5F6673]">
                  {isMentee ? (
                    <>
                      {lang?.mentorship_notif_request_as_mentee_prefix ||
                        "Your mentorship request to"}{" "}
                      {renderRequestUsers()}{" "}
                      {lang?.mentorship_notif_request_as_mentee_suffix ||
                        "has been submitted successfully."}
                    </>
                  ) : (
                    <>
                      {renderRequestUsers()}{" "}
                      {lang?.mentorship_notif_request_suffix_single ||
                        "has sent you a mentorship request and is awaiting your response."}
                    </>
                  )}
                </p>
              ) : isReminder ? (
                <p className="mb-0 fs-14 text-[#5F6673]">
                  {lang?.mentorship_notif_reminder_prefix || "Your session with"}{" "}
                  <span className="fw-500" style={{ color: "#00BADA" }}>
                    {capitalizeWords(user?.name)}
                  </span>{" "}
                  {lang?.mentorship_notif_reminder_middle ||
                    "is scheduled for today at"}{" "}
                  {formatTimeOnly(sessionStartTimestamp)}
                  {". "}
                  {lang?.mentorship_notif_reminder_suffix || "Get ready to join."}
                </p>
              ) : (
                value.data.body && (
                  <p className="mb-0 fs-md-14 text-secondary">{value.data.body}</p>
                )
              )}
            </div>
          </div>
          <small className="text-secondary flex-shrink-0">
            {formatNotificationTime(value.data.time, lang)}
          </small>
        </div>
      </div>
    </div>
  );
};

// Matches NotificationCardMentor's layout (circular avatar, role/reminder
// pill, title, body line, top-right time bar) instead of the generic
// NotificationSkeletonWave shape — used for both the mentor tab's initial
// load and its infinite-scroll "loading more" shimmer.
const NotificationCardMentorSkeleton = () => (
  <div
    className="border border-1 border-inherit mb-3 overflow-hidden"
    style={{ borderRadius: "20px" }}
  >
    <div className="px-3 py-3">
      <div className="d-flex justify-content-between align-items-start gap-2">
        <div className="d-flex gap-3">
          <div
            className="rounded-circle skeleton-wave flex-shrink-0"
            style={{ width: "3rem", height: "3rem" }}
          />
          <div>
            <div
              className="skeleton-wave rounded-pill mb-2"
              style={{ width: "5.5rem", height: "1.1rem" }}
            />
            <div
              className="skeleton-wave mb-2"
              style={{ width: "9rem", height: "0.9rem", borderRadius: "0.25rem" }}
            />
            <div
              className="skeleton-wave"
              style={{ width: "15rem", height: "0.75rem", borderRadius: "0.25rem" }}
            />
          </div>
        </div>
        <div
          className="skeleton-wave flex-shrink-0"
          style={{ width: "3.5rem", height: "0.75rem", borderRadius: "0.25rem" }}
        />
      </div>
    </div>
  </div>
);